import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import ServiceOrderCard from './components/ServiceOrderCard';
import TimeTracker from './components/TimeTracker';
import QuickActions from './components/QuickActions';
import TechnicianStats from './components/TechnicianStats';
import PhotoUploadModal from './components/PhotoUploadModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TechnicianMobile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [activeOrder, setActiveOrder] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [selectedOrderForPhoto, setSelectedOrderForPhoto] = useState(null);
  const [isOffline, setIsOffline] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(new Date());

  // Mock data for service orders
  const [serviceOrders, setServiceOrders] = useState([
    {
      id: "2024-001",
      clientName: "ABC Corporation",
      clientPhone: "(11) 98765-4321",
      serviceType: "Manutenção Preventiva",
      location: "Av. Paulista, 1000 - São Paulo/SP",
      coordinates: { lat: -23.5505, lng: -46.6333 },
      priority: "alta",
      status: "pendente",
      estimatedValue: 450.00,
      scheduledDate: "31/10/2024",
      scheduledTime: "14:00",
      description: `Realizar manutenção preventiva completa nos equipamentos de TI da empresa.\nVerificar servidores, switches e estações de trabalho.\nAtualizar documentação técnica.`,
      equipment: ["Servidor Dell PowerEdge", "Switch Cisco 24 portas", "Workstations (15 unidades)"],
      notes: "Cliente solicitou prioridade para o servidor principal. Acesso pelo portão lateral.",
      elapsedTime: 0
    },
    {
      id: "2024-002",
      clientName: "Tech Solutions Ltda",
      clientPhone: "(11) 91234-5678",
      serviceType: "Instalação de Equipamentos",
      location: "Rua Augusta, 500 - São Paulo/SP",
      coordinates: { lat: -23.5489, lng: -46.6388 },
      priority: "media",
      status: "em_andamento",
      estimatedValue: 320.00,
      scheduledDate: "31/10/2024",
      scheduledTime: "09:00",
      description: `Instalação de novos equipamentos de rede.\nConfiguração de switches e access points.\nTeste de conectividade e performance.`,
      equipment: ["Switch TP-Link 48 portas", "Access Point Ubiquiti (3 unidades)", "Cabos de rede"],
      notes: "Instalação no 3º andar. Coordenar com o responsável de TI local.",
      elapsedTime: 7200
    },
    {
      id: "2024-003",
      clientName: "Consultoria Empresarial",
      clientPhone: "(11) 95555-1234",
      serviceType: "Suporte Técnico",
      location: "Av. Faria Lima, 2000 - São Paulo/SP",
      coordinates: { lat: -23.5733, lng: -46.6892 },
      priority: "baixa",
      status: "pausada",
      estimatedValue: 180.00,
      scheduledDate: "31/10/2024",
      scheduledTime: "16:30",
      description: `Suporte técnico para resolução de problemas de conectividade.\nDiagnóstico de rede e correção de falhas.\nTreinamento básico para usuários.`,
      equipment: ["Notebook para diagnóstico", "Testador de cabos", "Kit de ferramentas"],
      notes: "Problema intermitente na rede. Usuários relatam lentidão.",
      elapsedTime: 3600
    }
  ]);

  // Mock technician stats
  const [technicianStats, setTechnicianStats] = useState({
    ordersToday: 3,
    ordersCompleted: 1,
    hoursWorked: 6.5,
    earnings: 487.50,
    rating: 4.8,
    efficiency: 92
  });

  // Check for active order on component mount
  useEffect(() => {
    const activeOrderData = serviceOrders?.find(order => order?.status === 'em_andamento');
    setActiveOrder(activeOrderData);
  }, [serviceOrders]);

  // Handle network status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleStartTimer = (order) => {
    setServiceOrders(prev => prev?.map(o => 
      o?.id === order?.id 
        ? { ...o, status: 'em_andamento' }
        : { ...o, status: o?.status === 'em_andamento' ? 'pausada' : o?.status }
    ));
    setActiveOrder({ ...order, status: 'em_andamento' });
  };

  const handlePauseTimer = (order) => {
    setServiceOrders(prev => prev?.map(o => 
      o?.id === order?.id ? { ...o, status: 'pausada' } : o
    ));
    setActiveOrder({ ...order, status: 'pausada' });
  };

  const handleStopTimer = (order) => {
    setServiceOrders(prev => prev?.map(o => 
      o?.id === order?.id ? { ...o, status: 'pendente', elapsedTime: 0 } : o
    ));
    setActiveOrder(null);
  };

  const handleCompleteOrder = (order) => {
    const confirmed = window.confirm(
      `Confirmar conclusão da OS #${order?.id}?\n\nEsta ação não pode ser desfeita.`
    );
    
    if (confirmed) {
      setServiceOrders(prev => prev?.map(o => 
        o?.id === order?.id ? { ...o, status: 'concluida' } : o
      ));
      setActiveOrder(null);
      
      // Update stats
      setTechnicianStats(prev => ({
        ...prev,
        ordersCompleted: prev?.ordersCompleted + 1,
        earnings: prev?.earnings + order?.estimatedValue
      }));
    }
  };

  const handleContactClient = (order) => {
    const confirmed = window.confirm(
      `Ligar para ${order?.clientName}?\n\nTelefone: ${order?.clientPhone}`
    );
    
    if (confirmed) {
      window.open(`tel:${order?.clientPhone?.replace(/\D/g, '')}`);
    }
  };

  const handleUploadPhoto = (order) => {
    setSelectedOrderForPhoto(order);
    setIsPhotoModalOpen(true);
  };

  const handlePhotoUpload = async (uploadData) => {
    // Simulate upload process
    console.log('Uploading photos for order:', uploadData?.orderId);
    console.log('Files:', uploadData?.files);
    console.log('Description:', uploadData?.description);
    
    // In a real app, this would upload to a server
    await new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handleEmergency = () => {
    console.log('Emergency protocol activated');
    // In a real app, this would trigger emergency procedures
    alert('Protocolo de emergência ativado!\nCentral notificada e localização enviada.');
  };

  const handleSyncData = async () => {
    // Simulate data sync
    await new Promise(resolve => setTimeout(resolve, 1500));
    setLastSyncTime(new Date());
  };

  const handleNavigateToLocation = () => {
    if (activeOrder && activeOrder?.coordinates) {
      const { lat, lng } = activeOrder?.coordinates;
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      window.open(url, '_blank');
    } else {
      alert('Nenhuma ordem ativa com localização disponível.');
    }
  };

  const handleCallSupport = () => {
    const confirmed = window.confirm(
      'Ligar para o suporte técnico?\n\nTelefone: (11) 3000-1234'
    );
    
    if (confirmed) {
      window.open('tel:+551130001234');
    }
  };

  const handleViewReports = () => {
    navigate('/service-orders');
  };

  const filteredOrders = serviceOrders?.filter(order => {
    switch (activeTab) {
      case 'pending':
        return order?.status === 'pendente';
      case 'active':
        return order?.status === 'em_andamento' || order?.status === 'pausada';
      case 'completed':
        return order?.status === 'concluida';
      default:
        return true;
    }
  });

  const tabItems = [
    { key: 'orders', label: 'Todas', count: serviceOrders?.length },
    { key: 'pending', label: 'Pendentes', count: serviceOrders?.filter(o => o?.status === 'pendente')?.length },
    { key: 'active', label: 'Ativas', count: serviceOrders?.filter(o => o?.status === 'em_andamento' || o?.status === 'pausada')?.length },
    { key: 'completed', label: 'Concluídas', count: serviceOrders?.filter(o => o?.status === 'concluida')?.length }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-20 lg:pb-6">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Mobile Técnico
              </h1>
              <p className="text-sm text-muted-foreground">
                {new Date()?.toLocaleDateString('pt-BR', { 
                  weekday: 'long', 
                  day: '2-digit', 
                  month: 'long' 
                })}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleSyncData()}
              className="relative"
            >
              <Icon name="RefreshCw" size={20} />
              {isOffline && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-error rounded-full"></div>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Time Tracker & Stats */}
          <div className="lg:col-span-1 space-y-6">
            <TimeTracker
              activeOrder={activeOrder}
              totalHoursToday={technicianStats?.hoursWorked}
              onStartTimer={handleStartTimer}
              onPauseTimer={handlePauseTimer}
              onStopTimer={handleStopTimer}
            />
            
            <TechnicianStats stats={technicianStats} />
            
            <QuickActions
              onEmergency={handleEmergency}
              onSyncData={handleSyncData}
              onNavigateToLocation={handleNavigateToLocation}
              onCallSupport={handleCallSupport}
              onViewReports={handleViewReports}
              isOffline={isOffline}
              lastSyncTime={lastSyncTime}
            />
          </div>

          {/* Right Column - Service Orders */}
          <div className="lg:col-span-2">
            {/* Tab Navigation */}
            <div className="bg-card border border-border rounded-lg shadow-professional mb-6">
              <div className="flex overflow-x-auto">
                {tabItems?.map((tab) => (
                  <button
                    key={tab?.key}
                    onClick={() => setActiveTab(tab?.key)}
                    className={`flex-1 min-w-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab?.key
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span className="truncate">{tab?.label}</span>
                      {tab?.count > 0 && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          activeTab === tab?.key
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {tab?.count}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Service Orders List */}
            <div className="space-y-4">
              {filteredOrders?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg shadow-professional p-8 text-center">
                  <Icon name="ClipboardList" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="font-semibold text-card-foreground mb-2">
                    Nenhuma ordem encontrada
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {activeTab === 'pending' && 'Não há ordens pendentes no momento.'}
                    {activeTab === 'active' && 'Não há ordens ativas no momento.'}
                    {activeTab === 'completed' && 'Não há ordens concluídas hoje.'}
                    {activeTab === 'orders' && 'Não há ordens de serviço disponíveis.'}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/service-orders')}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Ver Todas as Ordens
                  </Button>
                </div>
              ) : (
                filteredOrders?.map((order) => (
                  <ServiceOrderCard
                    key={order?.id}
                    order={order}
                    onStartTimer={handleStartTimer}
                    onPauseTimer={handlePauseTimer}
                    onCompleteOrder={handleCompleteOrder}
                    onContactClient={handleContactClient}
                    onUploadPhoto={handleUploadPhoto}
                    isTimerActive={activeOrder?.id === order?.id && order?.status === 'em_andamento'}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={isPhotoModalOpen}
        onClose={() => setIsPhotoModalOpen(false)}
        onUpload={handlePhotoUpload}
        orderInfo={selectedOrderForPhoto}
      />
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        userRole="technician"
        activeOrders={serviceOrders?.filter(o => o?.status === 'em_andamento' || o?.status === 'pausada')?.length}
        isVisible={true}
      />
    </div>
  );
};

export default TechnicianMobile;